import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Select,
  Pagination,
  Spin,
  Empty,
} from 'antd';
import { PlusOutlined, ReloadOutlined, RobotOutlined } from '@ant-design/icons';
import axios from '../../api';
import theme from '../../theme';
import permissionManager from '../../utils/permissions';
import AgentMemberManage from './AgentMemberManage';
import AgentInputBindings from './AgentInputBindings';
import AgentManageCard from './AgentManageCard';
import './agent-list.css';

const AgentList = () => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingAgent, setEditingAgent] = useState(null);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });

  const canCreate = permissionManager.hasPermission('agent', 'create');
  const canEdit = permissionManager.hasPermission('agent', 'edit');
  const canDelete = permissionManager.hasPermission('agent', 'delete');
  const canView = permissionManager.hasModulePermission('agent');
  const canManageMembers = canEdit || permissionManager.hasPermission('agent', 'create');

  const [agents, setAgents] = useState([]);
  const [groupByLanguage, setGroupByLanguage] = useState(false);
  const watchedApikey = Form.useWatch('apikey', form);

  const fetchAgents = async (page = 1, pageSize = 12) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (groupByLanguage) {
        params.group_by_name = 'true';
      }
      const response = await axios.get('/api/agents/', { params });
      if (Array.isArray(response.data)) {
        setAgents(response.data);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: response.data.length,
        }));
      } else {
        const list = response.data?.results ?? [];
        setAgents(Array.isArray(list) ? list : []);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: response.data?.count ?? list.length,
        }));
      }
    } catch (error) {
      console.error('获取智能体列表失败:', error);
      message.error('获取智能体列表失败');
      setAgents([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents(1, pagination.pageSize);
  }, [groupByLanguage]);

  const handlePageChange = (page, pageSize) => {
    fetchAgents(page, pageSize);
  };

  const handleAddEdit = (record = null) => {
    setEditingAgent(record);
    if (record) {
      form.setFieldsValue({
        ...record,
        input_bindings: record.input_bindings || {},
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ input_bindings: {}, language: 'zh-hans' });
    }
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const inputBindings = values.input_bindings && typeof values.input_bindings === 'object'
        ? values.input_bindings
        : {};
      setLoading(true);

      const payload = {
        name: values.name,
        apikey: values.apikey,
        api_url: values.api_url || null,
        intro: values.intro || null,
        avatar: values.avatar || null,
        language: values.language || 'zh-hans',
        role: values.role || null,
        input_bindings: inputBindings,
      };

      if (editingAgent) {
        await axios.put(`/api/agents/${editingAgent.id}/`, payload);
        message.success('智能体更新成功');
      } else {
        await axios.post('/api/agents/', payload);
        message.success('智能体创建成功');
      }

      setModalVisible(false);
      fetchAgents(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error('保存智能体失败:', err);
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确定要删除这个智能体吗？',
      content: `将删除「${record.name}」，此操作不可恢复`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          await axios.delete(`/api/agents/${record.id}/`);
          message.success('智能体删除成功');
          fetchAgents(pagination.current, pagination.pageSize);
        } catch (err) {
          console.error('删除智能体失败:', err);
          message.error('删除失败');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleAccessExternal = (record) => {
    const url = record.api_url || record.external_url;
    if (url) {
      window.open(url, '_blank');
    } else {
      message.info('外部服务链接未配置');
    }
  };

  const handleMemberManage = (record) => {
    setCurrentAgentId(record.id);
  };

  const handleBackToList = () => {
    setCurrentAgentId(null);
  };

  if (!canView) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#888', fontSize: 20 }}>
        🚫 您没有访问智能体管理的权限
      </div>
    );
  }

  if (currentAgentId) {
    return (
      <AgentMemberManage
        agentId={currentAgentId}
        onBack={handleBackToList}
        showTitle
      />
    );
  }

  return (
    <div className="agent-manage-page">
      <div className="agent-manage-panel">
        <div className="agent-manage-panel__toolbar">
          <h2 className="agent-manage-panel__title">智能体列表</h2>
          <Space wrap>
            <Button
              type={groupByLanguage ? 'primary' : 'default'}
              onClick={() => setGroupByLanguage(!groupByLanguage)}
              style={{
                background: groupByLanguage ? theme.BUTTON_BG : '#f5f5f5',
                color: groupByLanguage ? '#231a17' : '#666',
              }}
            >
              {groupByLanguage ? '聚合显示' : '普通显示'}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchAgents(pagination.current, pagination.pageSize)}
              loading={loading}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleAddEdit()}
              disabled={!canCreate}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              创建智能体
            </Button>
          </Space>
        </div>

        <div className="agent-manage-grid-wrap">
          <Spin spinning={loading}>
            {agents.length > 0 ? (
              <div className="agent-manage-grid">
                {agents.map((agent) => (
                  <AgentManageCard
                    key={agent.id}
                    agent={agent}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    canManageMembers={canManageMembers}
                    onEdit={handleAddEdit}
                    onDelete={handleDelete}
                    onAccess={handleAccessExternal}
                    onManageMembers={handleMemberManage}
                  />
                ))}
              </div>
            ) : (
              !loading && (
                <div className="agent-manage-empty">
                  <RobotOutlined style={{ fontSize: 40, opacity: 0.35 }} />
                  <Empty description="暂无智能体，点击右上角创建" />
                </div>
              )
            )}
          </Spin>
        </div>

        {pagination.total > 0 && (
          <div className="agent-manage-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={[12, 24, 48, 96]}
              showTotal={(total) => `共 ${total} 条`}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>

      <Modal
        title={editingAgent ? '编辑智能体' : '新增智能体'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        destroyOnHidden
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入智能体名称" allowClear />
          </Form.Item>
          <Form.Item name="apikey" label="API-key" rules={[{ required: true, message: '请输入API-key' }]}>
            <Input placeholder="Dify 应用的 app-xxx 密钥" allowClear />
          </Form.Item>
          <Form.Item name="api_url" label="API URL">
            <Input placeholder="如 https://kb.blooart.cn/v1/chat-messages" allowClear />
          </Form.Item>
          <Form.Item name="language" label="语言" initialValue="zh-hans">
            <Select options={[
              { value: 'zh-hans', label: '简体中文' },
              { value: 'en', label: 'English' },
              { value: 'fr', label: 'Français' },
            ]} />
          </Form.Item>
          <Form.Item name="intro" label="对话开场白">
            <Input.TextArea rows={2} placeholder="学生端欢迎语" allowClear />
          </Form.Item>
          <Form.Item name="avatar" label="头像 URL">
            <Input placeholder="https://..." allowClear />
          </Form.Item>
          <Form.Item name="role" label="角色标签">
            <Input placeholder="如：课程答疑、签证咨询（显示在卡片上）" allowClear />
          </Form.Item>
          <Form.Item
            label="用户参数绑定"
            tooltip="先填 API-key，再从 Dify 加载变量后选择绑定；选「用户 UID」表示每个学生会自动带入各自的 ID"
          >
            <Form.Item name="input_bindings" noStyle>
              <AgentInputBindings
                apikey={watchedApikey}
                agentId={editingAgent?.id}
              />
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentList;
